/**
 * Static Flashcard Service
 * Lưu/load flashcards từ static JSON files trong public/data/
 * Giải pháp cho WordPress hosting (không cần backend API)
 */

import type { Flashcard, PDFDocument } from '../../types/flashcard'

interface UserFlashcardData {
  userEmail: string
  documents: PDFDocument[]
  flashcards: Flashcard[]
  lastUpdated: string
}

class StaticFlashcardService {
  private readonly BASE_URL = '/data'
  
  /**
   * Sanitize email để làm filename
   */
  private sanitizeEmail(email: string): string {
    return email.replace(/[^a-z0-9@.-]/gi, '_')
  }

  /**
   * Get filename cho user
   */
  private getUserFilename(email: string): string {
    return `${this.sanitizeEmail(email)}.json`
  }

  /**
   * Load flashcards từ static file
   */
  async loadUserData(userEmail: string): Promise<UserFlashcardData | null> {
    try {
      const filename = this.getUserFilename(userEmail)
      const response = await fetch(`${this.BASE_URL}/${filename}`)
      
      if (!response.ok) {
        console.log(`No static data found for ${userEmail}`)
        return null
      }

      const data = await response.json()
      
      // Convert date strings to Date objects
      const flashcards = data.flashcards.map((f: any) => ({
        ...f,
        createdAt: new Date(f.createdAt),
        updatedAt: new Date(f.updatedAt),
        lastReviewed: f.lastReviewed ? new Date(f.lastReviewed) : undefined,
        nextReview: f.nextReview ? new Date(f.nextReview) : undefined
      }))

      const documents = data.documents.map((d: any) => ({
        ...d,
        uploadedAt: new Date(d.uploadedAt)
      }))

      console.log(`✅ Loaded ${flashcards.length} flashcards for ${userEmail} from static file`)
      
      return {
        ...data,
        flashcards,
        documents
      }
    } catch (error) {
      console.error('Error loading static flashcard data:', error)
      return null
    }
  }

  /**
   * Export dữ liệu hiện tại thành JSON để lưu vào public/data/
   * Dùng trong development để tạo file static
   */
  exportCurrentData(userEmail: string, flashcards: Flashcard[], documents: PDFDocument[]): string {
    const data: UserFlashcardData = {
      userEmail,
      documents,
      flashcards,
      lastUpdated: new Date().toISOString()
    }

    const json = JSON.stringify(data, null, 2)
    
    console.log(`📦 Exported data for ${userEmail}:`)
    console.log(`- ${flashcards.length} flashcards`)
    console.log(`- ${documents.length} documents`)
    console.log(`\nSave this to: public/data/${this.getUserFilename(userEmail)}`)
    
    return json
  }

  /**
   * Download export file (browser)
   */
  downloadExport(userEmail: string, flashcards: Flashcard[], documents: PDFDocument[]): void {
    const json = this.exportCurrentData(userEmail, flashcards, documents)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    
    const a = document.createElement('a')
    a.href = url
    a.download = this.getUserFilename(userEmail)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    console.log(`✅ Downloaded: ${this.getUserFilename(userEmail)}`)
  }

  /**
   * Migrate từ localStorage sang static file
   * Run này trong DevTools Console để tạo file export
   */
  async migrateFromLocalStorage(userEmail: string): Promise<string> {
    try {
      // Load from localStorage
      const flashcardsData = localStorage.getItem('vat_li_365_flashcards')
      const documentsData = localStorage.getItem('vat_li_365_pdf_documents')
      
      if (!flashcardsData && !documentsData) {
        throw new Error('No data found in localStorage')
      }

      const flashcards: Flashcard[] = flashcardsData 
        ? JSON.parse(flashcardsData).map((f: any) => ({
            ...f,
            createdAt: new Date(f.createdAt),
            updatedAt: new Date(f.updatedAt),
            lastReviewed: f.lastReviewed ? new Date(f.lastReviewed) : undefined,
            nextReview: f.nextReview ? new Date(f.nextReview) : undefined
          }))
        : []

      const documents: PDFDocument[] = documentsData
        ? JSON.parse(documentsData).map((d: any) => ({
            ...d,
            uploadedAt: new Date(d.uploadedAt)
          }))
        : []

      console.log(`🔄 Migrating from localStorage:`)
      console.log(`- Found ${flashcards.length} flashcards`)
      console.log(`- Found ${documents.length} documents`)

      const json = this.exportCurrentData(userEmail, flashcards, documents)
      
      // Auto download
      this.downloadExport(userEmail, flashcards, documents)
      
      return json
    } catch (error) {
      console.error('Migration error:', error)
      throw error
    }
  }

  /**
   * Check if static file exists for user
   */
  async hasStaticData(userEmail: string): Promise<boolean> {
    try {
      const filename = this.getUserFilename(userEmail)
      const response = await fetch(`${this.BASE_URL}/${filename}`, { method: 'HEAD' })
      return response.ok
    } catch {
      return false
    }
  }
}

export const staticFlashcardService = new StaticFlashcardService()

// Expose to window for DevTools access
if (typeof window !== 'undefined') {
  (window as any).staticFlashcardService = staticFlashcardService
}

